import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  AlertTriangle,
  CheckCircle2,
  ExternalLink,
  Play,
  RefreshCw,
  XCircle,
} from "lucide-react";
import { useGetList, useNotify } from "ra-core";
import { useCallback, useEffect, useState } from "react";

/**
 * Pipeline overview — did it run, and what did it change?
 *
 * WHY DELTAS RATHER THAN COUNTS
 * -----------------------------
 * "84,336 permits" tells you nothing on its own. The number that carries
 * information is what a run MOVED, which is why pipeline_run_summary derives
 * deltas with lag() instead of storing them -- derived, they cannot disagree
 * with the counts they came from.
 *
 * A null delta is shown as "--", not 0. There is no previous run to compare
 * against, and 0 would falsely claim nothing changed.
 *
 * WHY collection_health IS SEPARATE FROM THE RUN LIST
 * --------------------------------------------------
 * A list of runs cannot show the failure that matters most: runs that stopped
 * happening. A job that is no longer scheduled writes no 'failed' row, it
 * simply writes nothing, and an empty list looks like a quiet week. That is
 * what NOT CHECKING exists for, and why it is read straight from the database
 * rather than inferred here.
 */

type RunRow = {
  id: number;
  job: string;
  status: string;
  started_at: string;
  finished_at: string | null;
  duration_s: number | null;
  data_as_of: string | null;
  permits: number | null;
  addresses: number | null;
  mailable: number | null;
  review_open: number | null;
  permits_delta: number | null;
  addresses_delta: number | null;
  mailable_delta: number | null;
  review_delta: number | null;
  detail: string | null;
  run_url: string | null;
};

type Health = {
  health: string;
  last_successful_check: string | null;
  verified_current_through: string | null;
  ttb_published_date: string | null;
};

/** A signed delta, or "--" when there is no previous run to compare against. */
const Delta = ({ n }: { n: number | null }) => {
  if (n === null || n === undefined)
    return <span className="text-muted-foreground">--</span>;
  if (n === 0) return <span className="text-muted-foreground">0</span>;
  return (
    <span
      className={n > 0 ? "text-foreground font-medium" : "text-destructive"}
    >
      {n > 0 ? "+" : ""}
      {n.toLocaleString()}
    </span>
  );
};

const HEALTH_COPY: Record<string, string> = {
  ok: "Collection is current and verified.",
  "NOT CHECKING":
    "No successful check in 6 days. The scheduler has stopped — this is the alarm that does not depend on the collector working.",
  "BEHIND TTB":
    "TTB has published data we have neither taken nor proven we already hold.",
  UNVERIFIED:
    "304s have been trusted for over 10 days without a forced full fetch.",
};

/**
 * The three things the Overview could not answer.
 *
 * It showed pipeline runs and collection health -- whether DATA is arriving --
 * and nothing about whether anything is going OUT. Sending, address validation
 * and delivery outcomes were all invisible on the page people land on first.
 *
 * Read through the supabase client rather than getList: all three are
 * single-row roll-ups with no id column, and adding a synthetic id to a view
 * purely to suit one client is the wrong direction.
 */
const CampaignState = () => {
  const [validation, setValidation] = useState<Record<string, number> | null>(
    null,
  );
  const [outcomes, setOutcomes] = useState<Record<
    string,
    number | string | null
  > | null>(null);
  const [spend, setSpend] = useState<
    {
      estimated_cents: number | null;
      invoiced_cents: number | null;
      cards_sent: number;
      sent_without_invoice: number;
    }[]
  >([]);
  const [attempts, setAttempts] = useState<
    {
      id: number;
      cell: string;
      touch: string;
      vendor: string;
      mode: string;
      environment: string | null;
      ok: boolean;
      attempted_at: string;
      estimated_cost_cents: number | null;
    }[]
  >([]);

  useEffect(() => {
    (async () => {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const db = getSupabaseClient();
      const [v, o, s, a] = await Promise.all([
        db.from("validation_progress").select("*").single(),
        db.from("mail_outcome_summary").select("*").single(),
        db.from("campaign_spend").select("*"),
        db
          .from("send_attempts")
          .select("*")
          .order("attempted_at", { ascending: false })
          .limit(3),
      ]);
      setValidation(v.data ?? null);
      setOutcomes(o.data ?? null);
      setSpend(s.data ?? []);
      setAttempts(a.data ?? []);
    })().catch(() => {});
  }, []);

  const notify = useNotify();
  const [syncing, setSyncing] = useState(false);

  /**
   * Ask PCM what happened to mail we sent.
   *
   * A pull rather than their webhook, for now: the webhook is richer but needs
   * an endpoint registered with PCM, and it cannot tell you about mail that
   * moved before it existed. This covers the outcome that costs money and needs
   * no arrangement.
   */
  const sync = async () => {
    setSyncing(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke(
        "pcm-sync",
        {
          body: {},
        },
      );
      if (error) {
        const ctx = (error as { context?: Response }).context;
        const b = ctx?.json ? await ctx.json().catch(() => null) : null;
        throw new Error(b?.error ?? error.message);
      }
      const r = data as {
        undeliverable_reported?: number;
        recorded?: number;
        already_known?: number;
        invoices_found?: number;
      };
      notify(
        `PCM reported ${r?.undeliverable_reported ?? 0} undeliverable · ` +
          `${r?.recorded ?? 0} new, ${r?.already_known ?? 0} already known` +
          (r?.invoices_found ? ` · ${r.invoices_found} invoice(s) linked` : ""),
        { type: "info" },
      );
    } catch (e) {
      notify((e as Error).message, { type: "error" });
    } finally {
      setSyncing(false);
    }
  };

  const money = (cents: number | null | undefined) =>
    cents == null ? "—" : `$${(cents / 100).toFixed(2)}`;

  const estimated = spend.reduce((n, r) => n + (r.estimated_cents ?? 0), 0);
  const invoiced = spend.reduce((n, r) => n + (r.invoiced_cents ?? 0), 0);
  const unreconciled = spend.reduce(
    (n, r) => n + (r.sent_without_invoice ?? 0),
    0,
  );
  const cardsSent = spend.reduce((n, r) => n + (r.cards_sent ?? 0), 0);

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <Card>
        <CardContent className="pt-4">
          <h3 className="text-sm font-medium">Sending</h3>
          <p className="text-2xl font-semibold mt-1 tabular-nums">
            {cardsSent}
          </p>
          <p className="text-xs text-muted-foreground">cards posted</p>
          {/*
            ESTIMATED AND INVOICED ARE NEVER ADDED TOGETHER. Only one of them is
            evidence: PCM returns no cost at order time, so the estimate is a
            rate-card guess and a single blended figure would present it as a
            receipt.
          */}
          <div className="text-xs mt-2 space-y-0.5">
            <div>
              estimated <b>{money(estimated)}</b>
            </div>
            <div>
              invoiced <b>{money(invoiced || null)}</b>
              {unreconciled > 0 && (
                <span className="text-muted-foreground">
                  {" "}
                  · {unreconciled} card{unreconciled === 1 ? "" : "s"} not yet
                  reconciled
                </span>
              )}
            </div>
          </div>
          {attempts.length > 0 && (
            <div className="text-xs text-muted-foreground mt-2 pt-2 border-t space-y-0.5">
              {attempts.map((a) => (
                <div key={a.id}>
                  {a.cell} {a.touch} · {a.vendor}{" "}
                  {a.mode === "test" ? (a.environment ?? "test") : "live"}
                  {a.ok ? "" : " · failed"}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h3 className="text-sm font-medium">Address validation</h3>
          <p className="text-2xl font-semibold mt-1 tabular-nums">
            {validation ? validation.doors - validation.unchecked : "—"}
            <span className="text-sm text-muted-foreground font-normal">
              {" "}
              / {validation?.doors ?? "—"}
            </span>
          </p>
          <p className="text-xs text-muted-foreground">
            checked against a postal file
          </p>
          {validation && (
            <div className="text-xs mt-2 space-y-0.5">
              <div>
                {validation.validated} valid · {validation.undeliverable}{" "}
                undeliverable
              </div>
              {validation.errored > 0 && (
                <div className="text-destructive">
                  {validation.errored} errored — retried
                </div>
              )}
              {/* A "valid" result that rewrote the address is the interesting
                  half, and a bare status hides it. */}
              {validation.rewritten > 0 && (
                <div>{validation.rewritten} had their address rewritten</div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-4">
          <h3 className="text-sm font-medium">Delivery</h3>
          <p className="text-2xl font-semibold mt-1 tabular-nums">
            {outcomes?.delivered ?? 0}
          </p>
          <p className="text-xs text-muted-foreground">confirmed delivered</p>
          {outcomes && (
            <div className="text-xs mt-2 space-y-0.5">
              <div>
                {outcomes.returned} returned · {outcomes.undeliverable}{" "}
                undeliverable
              </div>
              {Number(outcomes.unmatched_events ?? 0) > 0 && (
                // Kept rather than hidden: an outcome about a door we cannot
                // find is the mismatch worth investigating.
                <div className="text-destructive">
                  {outcomes.unmatched_events} event(s) matched no door
                </div>
              )}
              {!outcomes.events && (
                <div className="text-muted-foreground">
                  Nothing reported yet. Outcomes arrive from PCM once real mail
                  moves.
                </div>
              )}
              <Button
                variant="outline"
                size="sm"
                className="mt-2"
                onClick={sync}
                disabled={syncing}
              >
                {syncing ? "Checking…" : "Check PCM for outcomes"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

/**
 * The job this page is about.
 *
 * Every scheduled job writes to `pipeline_runs`, so a query against it that
 * does not say which one gets whichever ran most recently -- and that is
 * never this one, because the drainer runs several times a day.
 */
const TTB_JOB = "ttb-refresh";

export const PipelineOverview = () => {
  const notify = useNotify();
  const [health, setHealth] = useState<Health | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [triggering, setTriggering] = useState(false);

  const {
    data: runs,
    isPending,
    error: runsErr,
    refetch,
  } = useGetList<RunRow>("pipeline_run_summary", {
    /*
      THE JOB FILTER IS THE WHOLE POINT OF THIS QUERY.

      `pipeline_runs` is shared by every scheduled job -- ttb-refresh,
      nurture-tick, nurture-digest and drain-outbound -- and
      pipeline_run_summary carries all of them; it even partitions its deltas
      BY job, so it was written knowing that. Without this filter the "last 3
      runs" were simply the three most recent rows of ANY job, and since the
      drainer runs several times a day while ttb-refresh runs Monday and
      Saturday, they were ALWAYS the drainer.

      The page then contradicted itself in a way that looked like a data
      problem rather than a display one: the health card said the collection
      was last checked on 31 August, which was true, while the list underneath
      showed three runs from today. The giveaway was every metric reading
      "— (- -)", because permits, addresses and mailable are TTB's columns and
      are null on a drainer row.
    */
    filter: { job: TTB_JOB },
    pagination: { page: 1, perPage: 3 },
    sort: { field: "started_at", order: "DESC" },
  });

  /**
   * collection_health is a single-row view with no id column, so react-admin's
   * getList cannot represent it. Read it through the same supabase client the
   * provider uses rather than adding a synthetic id to the view purely to suit
   * one client.
   */
  const loadHealth = useCallback(async () => {
    const { getSupabaseClient } = await import(
      "../providers/supabase/supabase"
    );
    const { data, error } = await getSupabaseClient()
      .from("collection_health")
      .select("*")
      .single();
    if (error) setHealthErr(error.message);
    else setHealth(data as Health);
  }, []);

  // Surface a rejected dynamic import or network failure rather than letting
  // the card sit on a skeleton indefinitely.
  const loadHealthSafe = useCallback(
    () => loadHealth().catch((e) => setHealthErr(String(e?.message ?? e))),
    [loadHealth],
  );

  useEffect(() => {
    loadHealthSafe();
  }, [loadHealthSafe]);

  const trigger = async (force: boolean) => {
    setTriggering(true);
    try {
      const { getSupabaseClient } = await import(
        "../providers/supabase/supabase"
      );
      const { data, error } = await getSupabaseClient().functions.invoke(
        "trigger-pipeline",
        { body: { force } },
      );
      /**
       * supabase-js reports ANY non-2xx as "Failed to send a request to the
       * Edge Function" and discards the response body, so a function that
       * answers 503 with a precise explanation looks identical to a network
       * outage. That cost real debugging time here: the function was returning
       * "GITHUB_TOKEN is not configured" and the UI showed a transport error.
       *
       * The body is still reachable on error.context, which is the raw
       * Response. Read it before falling back to the generic message.
       */
      if (error) {
        const res = (error as { context?: Response }).context;
        const body = res ? await res.json().catch(() => null) : null;
        throw new Error(body?.fix ?? body?.error ?? error.message);
      }
      if (data?.error) throw new Error(data.fix ?? data.error);
      notify(
        force
          ? "Full integrity fetch started on GitHub Actions."
          : "Pipeline run started on GitHub Actions.",
        { type: "info" },
      );
      // POLL, do not guess a delay.
      //
      // The first attempt at this refetched once after 8 seconds, which was
      // simply wrong: GitHub has to accept the dispatch, queue a runner, boot
      // it, install dependencies and only then does record_run.py write the
      // row. That took past 8s every time, so the list still showed the old
      // runs until the page was reloaded by hand -- the button appeared to do
      // nothing.
      //
      // Poll every 5s for two minutes and stop as soon as a row appears that
      // was not there when the button was pressed. Stopping on the new row
      // rather than running the full two minutes means no needless queries in
      // the ordinary case.
      const before = runs?.[0]?.id ?? null;
      let tries = 0;
      const timer = setInterval(async () => {
        tries += 1;
        const { data: fresh } = await refetch();
        // react-admin's refetch resolves to the raw query result, which is
        // {data, total} for getList -- not the array. Accept either shape
        // rather than trusting a cast: guessing wrong here does not fail
        // loudly, it just silently never matches and polls the full two
        // minutes.
        const rows = (
          Array.isArray(fresh)
            ? fresh
            : ((fresh as { data?: unknown } | undefined)?.data ?? [])
        ) as RunRow[];
        const newest = rows[0]?.id ?? null;
        if ((newest !== null && newest !== before) || tries >= 24) {
          clearInterval(timer);
          loadHealthSafe();
        }
      }, 5000);
    } catch (e) {
      notify(e instanceof Error ? e.message : "Could not start the run", {
        type: "error",
      });
    } finally {
      setTriggering(false);
    }
  };

  const ok = health?.health === "ok";
  const Icon = ok ? CheckCircle2 : AlertTriangle;

  return (
    <div className="space-y-4">
      {/* Whether anything is going OUT. The page answered only whether data was
          coming IN, which is half a pipeline. */}
      <CampaignState />

      {/* Health: it answers the question the run list structurally
          cannot, which is whether runs have stopped happening at all. */}
      <Card>
        <CardContent className="pt-4">
          {healthErr ? (
            <div className="text-sm text-destructive">
              Could not read collection_health: {healthErr}
            </div>
          ) : !health ? (
            <Skeleton className="h-12 w-full" />
          ) : (
            <div className="flex items-start gap-3">
              <Icon
                className={`size-5 shrink-0 mt-0.5 ${ok ? "text-muted-foreground" : "text-destructive"}`}
              />
              <div className="min-w-0">
                <div className="font-medium">
                  {ok ? "Collection healthy" : health.health}
                </div>
                <div className="text-sm text-muted-foreground">
                  {HEALTH_COPY[health.health] ?? health.health}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  TTB published {health.ttb_published_date ?? "—"} · verified
                  through {health.verified_current_through ?? "—"} · last check{" "}
                  {health.last_successful_check
                    ? new Date(health.last_successful_check).toLocaleString()
                    : "never"}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center gap-2 flex-wrap">
        <Button onClick={() => trigger(false)} disabled={triggering}>
          <Play className="size-4" /> Run now
        </Button>
        <Button
          variant="outline"
          onClick={() => trigger(true)}
          disabled={triggering}
        >
          <RefreshCw className="size-4" /> Force full fetch
        </Button>
        <span className="text-xs text-muted-foreground">
          Scheduled Monday and Saturday, 06:00 ET. A run with nothing new is one
          conditional request.
        </span>
      </div>

      <div>
        <h2 className="text-sm font-medium mb-2">Last 3 runs</h2>
        {runsErr ? (
          <p className="text-sm text-destructive">
            Could not load runs: {String((runsErr as Error).message ?? runsErr)}
          </p>
        ) : isPending ? (
          <Skeleton className="h-32 w-full" />
        ) : !runs?.length ? (
          <p className="text-sm text-muted-foreground">No runs recorded yet.</p>
        ) : (
          <div className="space-y-2">
            {runs.map((r) => (
              <Card key={r.id}>
                <CardContent className="pt-4 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    {r.status === "ok" ? (
                      <CheckCircle2 className="size-4 text-muted-foreground" />
                    ) : r.status === "failed" ? (
                      <XCircle className="size-4 text-destructive" />
                    ) : (
                      <RefreshCw className="size-4 animate-spin" />
                    )}
                    <span className="font-medium">
                      {new Date(r.started_at).toLocaleString()}
                    </span>
                    <Badge
                      variant={r.status === "ok" ? "outline" : "destructive"}
                    >
                      {r.status}
                    </Badge>
                    {r.duration_s != null ? (
                      <span className="text-xs text-muted-foreground">
                        {r.duration_s}s
                      </span>
                    ) : null}
                    {r.data_as_of ? (
                      <span className="text-xs text-muted-foreground">
                        data as of {r.data_as_of}
                      </span>
                    ) : null}
                    {r.run_url ? (
                      <a
                        href={r.run_url}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs underline inline-flex items-center gap-1 ml-auto"
                      >
                        log <ExternalLink className="size-3" />
                      </a>
                    ) : null}
                  </div>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    {(
                      [
                        ["Permits", r.permits, r.permits_delta],
                        ["Doors", r.addresses, r.addresses_delta],
                        ["Mailable", r.mailable, r.mailable_delta],
                        ["Review open", r.review_open, r.review_delta],
                      ] as const
                    ).map(([label, value, delta]) => (
                      <div key={label}>
                        <div className="text-xs text-muted-foreground">
                          {label}
                        </div>
                        <div className="tabular-nums">
                          {value?.toLocaleString() ?? "—"}{" "}
                          <span className="text-xs">
                            (<Delta n={delta} />)
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {r.detail ? (
                    <div className="text-xs text-muted-foreground">
                      {r.detail}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
